/**
 * DemoMetricsClient (PROD-592).
 *
 * Demo Mode implementation of the `MetricsClient` interface. Derives
 * every metric deterministically from the in-memory stores (NBA,
 * Override, Tenant) plus a canned snapshot fixture. The fixture
 * provides realistic baseline numbers so the dashboard renders
 * meaningful content during a clinician demo even when the live
 * stores carry zero events.
 *
 * Determinism (HC1 + HC4): every query resolves to the same value
 * for the same store snapshot. There is no `Math.random()` and no
 * wall-clock fan-out except an injected clock for the `fetchedAt`
 * timestamp. Tests inject a fixed clock so the snapshot is fully
 * reproducible.
 *
 * Live updates: the client reads the stores at call time, so any
 * mutation that has landed in `useNbaStore` / `useOverrideStore` /
 * `useTenantStore` is reflected on the next snapshot. The canned
 * baseline is added on top of the live counts so the demo never
 * shows the all-zero state.
 *
 * HC5: zero em / en dashes anywhere.
 */
import type { MetricsClient } from './MetricsClient';
import {
  DEFAULT_TIME_WINDOW,
  LIVE_LEARNING_EVENT_NAMES,
  type AzureStackRow,
  type ClinicIntelligenceProfileSummary,
  type ConfidencePoint,
  type LearningBullet,
  type LiveLearningEventName,
  type LiveSignalEntry,
  type MetricsSnapshot,
  type OverrideRate,
  type PersonalizationPoint,
  type SuggestionQualityRow,
} from './types';
import { useNbaStore } from '../../state/nba.store';
import { useOverrideStore } from '../../state/override.store';
import type { TenantId } from '../../state/types';
import type { NbaState } from '../../types/nba';

/**
 * Canned baseline for Live Learning Signals event counts. Values are
 * shaped to match a realistic 7-day rollout window: override events
 * dominate, mode switches are rare, lab-basket-confirmation events
 * are mid-volume.
 *
 * The canned values are added to the live in-memory mutation count so
 * the displayed numbers tick up as the user interacts during the
 * demo.
 */
export const DEMO_BASELINE_EVENT_COUNTS: Record<LiveLearningEventName, number> =
  {
    override_event: 124,
    mode_switch: 8,
    tenant_change: 14,
    nba_confidence_adjusted: 96,
    lab_basket_confirmation_required: 47,
  };

/**
 * Canned baseline confidence timeline per canonical NBA id. Demo Mode
 * overlays live `nba_confidence_adjusted` events on top of these
 * series so the chart shows both projection and any in-session
 * activity.
 *
 * The series are chronologically ordered; the most recent point is
 * last. ISO timestamps are anchored to the demo epoch (2026-04-29)
 * so the chart is stable across runs.
 */
export const DEMO_BASELINE_CONFIDENCE_TIMELINE: Record<
  string,
  ConfidencePoint[]
> = {
  'nba-tlp1-glp1-eligibility': [
    { ts: '2026-04-29T08:00:00.000Z', confidence: 0.92 },
    { ts: '2026-04-30T10:00:00.000Z', confidence: 0.86 },
    { ts: '2026-05-02T14:00:00.000Z', confidence: 0.74 },
    { ts: '2026-05-04T09:00:00.000Z', confidence: 0.62 },
    { ts: '2026-05-06T11:00:00.000Z', confidence: 0.55 },
  ],
  'nba-tlp2-hrt-baseline': [
    { ts: '2026-04-29T09:00:00.000Z', confidence: 0.88 },
    { ts: '2026-05-01T11:00:00.000Z', confidence: 0.78 },
    { ts: '2026-05-03T13:00:00.000Z', confidence: 0.72 },
    { ts: '2026-05-05T15:00:00.000Z', confidence: 0.7 },
  ],
  'nba-tlp3-metsyn-statin': [
    { ts: '2026-04-30T08:00:00.000Z', confidence: 0.81 },
    { ts: '2026-05-02T12:00:00.000Z', confidence: 0.75 },
    { ts: '2026-05-04T16:00:00.000Z', confidence: 0.68 },
  ],
  'nba-tlp4-cvd-apob': [
    { ts: '2026-04-29T10:00:00.000Z', confidence: 0.9 },
    { ts: '2026-05-01T12:00:00.000Z', confidence: 0.84 },
    { ts: '2026-05-03T14:00:00.000Z', confidence: 0.79 },
    { ts: '2026-05-05T16:00:00.000Z', confidence: 0.72 },
  ],
};

/**
 * Canned baseline tenant override-rate sparkline. Two tenants per
 * the Phase 1 fixture set; each tenant carries 7 daily points.
 */
export const DEMO_BASELINE_TENANT_TREND: Record<
  TenantId,
  PersonalizationPoint[]
> = {
  'apex-cardiometabolic': [
    { ts: '2026-04-30T00:00:00.000Z', rate: 0.18 },
    { ts: '2026-05-01T00:00:00.000Z', rate: 0.21 },
    { ts: '2026-05-02T00:00:00.000Z', rate: 0.19 },
    { ts: '2026-05-03T00:00:00.000Z', rate: 0.24 },
    { ts: '2026-05-04T00:00:00.000Z', rate: 0.27 },
    { ts: '2026-05-05T00:00:00.000Z', rate: 0.23 },
    { ts: '2026-05-06T00:00:00.000Z', rate: 0.29 },
  ],
  'longevity-health-institute': [
    { ts: '2026-04-30T00:00:00.000Z', rate: 0.12 },
    { ts: '2026-05-01T00:00:00.000Z', rate: 0.14 },
    { ts: '2026-05-02T00:00:00.000Z', rate: 0.13 },
    { ts: '2026-05-03T00:00:00.000Z', rate: 0.17 },
    { ts: '2026-05-04T00:00:00.000Z', rate: 0.18 },
    { ts: '2026-05-05T00:00:00.000Z', rate: 0.16 },
    { ts: '2026-05-06T00:00:00.000Z', rate: 0.21 },
  ],
};

/**
 * Total events indexed in the search index (`scrips-cc-index`) per the
 * Plan v9 §4.2 prototype rework. Anchored to the prototype's "847
 * events indexed" headline; deterministic so HC1 byte-identical
 * comparison holds across runs.
 */
export const DEMO_EVENTS_INDEXED_COUNT = 847;

/**
 * Total provenance writes in the Phase 1 baseline. Surfaced in the
 * Azure Stack footer.
 */
export const DEMO_PROVENANCE_WRITES_COUNT = 2_341;

/**
 * Live Signal Feed deterministic seed (Plan v9 §4.2). Eight rows in
 * reverse-timeline order so the prototype's "last 30 minutes" feel is
 * preserved without coupling to wall-clock time. Timestamps are
 * anchored to the demo epoch.
 */
export const DEMO_LIVE_SIGNALS: ReadonlyArray<LiveSignalEntry> = [
  {
    id: 'sig-001',
    ts: '2026-05-07T11:58:42.000Z',
    kind: 'override_captured',
    summary: 'Override captured: referral declined (BMI < 34, no DM)',
    detail: 'Patient pseudo-id pat-7842 . NBA suppressed for similar cohorts',
  },
  {
    id: 'sig-002',
    ts: '2026-05-07T11:55:11.000Z',
    kind: 'nba_approved',
    summary: 'NBA Approved: TSH lab order added to Cardiometabolic Pathway basket',
    detail: 'Confidence delta +0.04 . Pattern reinforced',
  },
  {
    id: 'sig-003',
    ts: '2026-05-07T11:52:03.000Z',
    kind: 'encounter_indexed',
    summary: 'Encounter indexed: SOAP note draft 1.2 KB embedded',
    detail: 'Patient pseudo-id pat-7841 . Outcome: visit completed',
  },
  {
    id: 'sig-004',
    ts: '2026-05-07T11:48:22.000Z',
    kind: 'prom_signal_ingested',
    summary: 'PROM signal ingested: MSQ score 18 (improved)',
    detail: 'JITAI rule reinforced for 24h post-enrolment cohort',
  },
  {
    id: 'sig-005',
    ts: '2026-05-07T11:44:51.000Z',
    kind: 'nba_approved',
    summary: 'NBA Approved: GLP-1 eligibility check confirmed',
    detail: 'Confidence delta +0.03 . Tenant signal added',
  },
  {
    id: 'sig-006',
    ts: '2026-05-07T11:41:09.000Z',
    kind: 'encounter_indexed',
    summary: 'Encounter indexed: telehealth follow-up captured',
    detail: 'Patient pseudo-id pat-7840 . Outcome: stable',
  },
  {
    id: 'sig-007',
    ts: '2026-05-07T11:37:33.000Z',
    kind: 'override_captured',
    summary: 'Override captured: TSH always co-ordered with Cardiometabolic Pathway labs',
    detail: 'Auto-include rule promoted to clinic protocol',
  },
  {
    id: 'sig-008',
    ts: '2026-05-07T11:34:58.000Z',
    kind: 'prom_signal_ingested',
    summary: 'PROM signal ingested: PHQ-9 score 6 (mild)',
    detail: 'Cohort: Metabolic Syndrome Pathway metabolic syndrome week 4',
  },
];

/**
 * Day 1 vs Today suggestion-quality rows (Plan v9 §4.2). Numbers
 * mirror the prototype: vanilla LLM at left, post-learning at right.
 */
export const DEMO_SUGGESTION_QUALITY_ROWS: ReadonlyArray<SuggestionQualityRow> = [
  { metric: 'NBA Accept Rate', day1: '38%', today: '79%', direction: 'improved' },
  { metric: 'Override Rate', day1: '49%', today: '14%', direction: 'improved' },
  { metric: 'Avg. Confidence', day1: '54%', today: '87%', direction: 'improved' },
  { metric: 'Time to Approve', day1: '8.2s', today: '2.1s', direction: 'improved' },
];

/**
 * Per-tenant Clinic Intelligence Profile summary (Plan v9 §4.2). The
 * Apex Cardiometabolic fixture mirrors the prototype's Dr. Al-Dhaheri
 * card; the Longevity Health Institute fixture is a complementary
 * second-tenant baseline so the dashboard renders meaningfully when
 * the tenant switcher flips.
 */
export const DEMO_CLINIC_INTELLIGENCE_BY_TENANT: Record<
  TenantId,
  ClinicIntelligenceProfileSummary
> = {
  'apex-cardiometabolic': {
    providerName: 'Dr. Al-Dhaheri',
    clinicLabel: 'Cardiometabolic Pathway',
    personalizationScore: 87,
    personalizationDelta: 49,
    precisionMetrics: [
      { label: 'NBA Precision', value: '87%', direction: 'improved' },
      { label: 'SOAP Acceptance', value: '91%', direction: 'improved' },
      { label: 'Override Reduction', value: '72%', direction: 'improved' },
      { label: 'PROM Completion', value: '94%', direction: 'improved' },
    ],
  },
  'longevity-health-institute': {
    providerName: 'Dr. Bensaada',
    clinicLabel: 'HRT Pathway',
    personalizationScore: 76,
    personalizationDelta: 38,
    precisionMetrics: [
      { label: 'NBA Precision', value: '78%', direction: 'improved' },
      { label: 'SOAP Acceptance', value: '85%', direction: 'improved' },
      { label: 'Override Reduction', value: '61%', direction: 'improved' },
      { label: 'PROM Completion', value: '88%', direction: 'improved' },
    ],
  },
};

/**
 * "What this clinic's AI has learned" bullets (Plan v9 §4.2). Three
 * entries per tenant, one per category (preference, protocol,
 * outcome). Apex Cardiometabolic mirrors the prototype's verbatim
 * bullets.
 */
export const DEMO_LEARNING_BULLETS_BY_TENANT: Record<
  TenantId,
  LearningBullet[]
> = {
  'apex-cardiometabolic': [
    {
      category: 'preference',
      text: 'This clinic keeps GLP-1 patients in-house rather than referring to endocrinology. Referral NBA now suppressed for BMI under 34 without DM.',
    },
    {
      category: 'protocol',
      text: 'Dr. Al-Dhaheri always orders TSH alongside standard Cardiometabolic Pathway labs. TSH now auto-included in basket without NBA prompt.',
    },
    {
      category: 'outcome',
      text: 'Patients who complete MSQ within 24h of Step 1 have 3x better 16-week adherence. JITAI timing adjusted to 6h post-enrolment.',
    },
  ],
  'longevity-health-institute': [
    {
      category: 'preference',
      text: 'This clinic prefers in-house DEXA scans. Imaging referral NBA now suppressed when DEXA appointment is scheduled within 14 days.',
    },
    {
      category: 'protocol',
      text: 'HRT Pathway baseline labs always include ApoB and Lp(a). Both auto-included in the order set without NBA prompt.',
    },
    {
      category: 'outcome',
      text: 'Patients who complete PHQ-9 + GAD-7 in the same session have 2.4x better 12-week retention. JITAI bundles the two surveys in the welcome pack.',
    },
  ],
};

/**
 * Azure Stack footer rows (Plan v9 §4.2). Mirror the prototype's
 * footer table verbatim. Mono cells render `ada-002` and
 * `scrips-cc-index` in monospace per HC2 token alignment.
 */
export const DEMO_AZURE_STACK_ROWS: ReadonlyArray<AzureStackRow> = [
  { label: 'Embedding model', value: 'ada-002', mono: true },
  { label: 'Search index', value: 'scrips-cc-index', mono: true },
  {
    label: 'Events indexed',
    value: DEMO_EVENTS_INDEXED_COUNT.toLocaleString('en-US'),
    mono: false,
  },
  {
    label: 'Provenance writes',
    value: DEMO_PROVENANCE_WRITES_COUNT.toLocaleString('en-US'),
    mono: false,
  },
  { label: 'Model retraining', value: 'Never needed', mono: false },
];

/**
 * Demo Mode constructor options. The clock is injected so tests pin
 * a deterministic `fetchedAt`. Production usage passes
 * `() => new Date().toISOString()`.
 */
export interface DemoMetricsClientOptions {
  /** Clock injection for the `fetchedAt` snapshot field. */
  now?: () => string;
}

const TENANT_IDS: ReadonlyArray<TenantId> = [
  'apex-cardiometabolic',
  'longevity-health-institute',
];

/**
 * Build the live event-count contribution from the in-memory stores.
 * The override store carries one entry per submitted override, so
 * `override_event` reads from there. Other event types are zero on
 * the live side because there is no in-memory ledger; the canned
 * baseline carries the realistic visible count.
 */
function liveEventCounts(): Record<LiveLearningEventName, number> {
  const overrideEvents = useOverrideStore.getState().events;
  const counts: Record<LiveLearningEventName, number> = {
    override_event: overrideEvents.length,
    mode_switch: 0,
    tenant_change: 0,
    nba_confidence_adjusted: 0,
    lab_basket_confirmation_required: 0,
  };
  return counts;
}

/**
 * Read the NBA candidate set across every patient. Returns a flat
 * array so the ratio computation is straightforward.
 */
function allNbaCandidates(): ReturnType<
  typeof useNbaStore.getState
>['candidatesByPatient'][string] {
  const map = useNbaStore.getState().candidatesByPatient;
  const flat: ReturnType<typeof useNbaStore.getState>['candidatesByPatient'][string] = [];
  for (const list of Object.values(map)) {
    for (const c of list) flat.push(c);
  }
  return flat;
}

/**
 * Build the NBA-state ratio. Empty candidate set returns all zeros
 * so the dashboard can render an empty state cleanly.
 */
function computeNbaStateRatio(): Record<NbaState, number> {
  const flat = allNbaCandidates();
  const ratio: Record<NbaState, number> = {
    active: 0,
    reduced: 0,
    restored: 0,
  };
  if (flat.length === 0) return ratio;
  const counts: Record<NbaState, number> = {
    active: 0,
    reduced: 0,
    restored: 0,
  };
  for (const c of flat) counts[c.state] += 1;
  // Round to 4 places so the values are deterministic across
  // floating-point arithmetic on different runtimes.
  ratio.active = Math.round((counts.active / flat.length) * 10_000) / 10_000;
  ratio.reduced = Math.round((counts.reduced / flat.length) * 10_000) / 10_000;
  ratio.restored =
    Math.round((counts.restored / flat.length) * 10_000) / 10_000;
  return ratio;
}

/**
 * Build the per-NBA before/after confidence map. Reads the override
 * store for the list of overridden NBA ids and the NBA store for the
 * post-decay confidence; the "before" value is reconstructed by
 * applying the inverse-decay relationship documented in
 * `services/learning-loop.ts` (multiply by 1 / decay factor, capped
 * at 1.0). Pure derivation; no live history needed.
 */
function computeBeforeAfterByNba(): Record<
  string,
  { before: number; after: number }
> {
  const overrides = useOverrideStore.getState().events;
  const flat = allNbaCandidates();
  const byId = new Map(flat.map((c) => [c.id, c]));
  const out: Record<string, { before: number; after: number }> = {};
  for (const ev of overrides) {
    const nba = byId.get(ev.nba_id);
    if (!nba) continue;
    const after = nba.confidence;
    // Inverse of the decay step (factor 0.6 from PROD-572).
    const before = Math.min(1, Math.round((after / 0.6) * 10_000) / 10_000);
    out[ev.nba_id] = { before, after };
  }
  return out;
}

/**
 * Build the per-tenant override rate. Rate is `overrides / candidates`
 * for the tenant; zero candidates returns zero (never NaN).
 */
function computeOverrideRateByTenant(): Record<TenantId, OverrideRate> {
  const flat = allNbaCandidates();
  const overrides = useOverrideStore.getState().events;
  const out: Record<TenantId, OverrideRate> = {
    'apex-cardiometabolic': 0,
    'longevity-health-institute': 0,
  };
  for (const tenant of TENANT_IDS) {
    const tenantCandidates = flat.filter((c) => c.tenantId === tenant);
    const tenantOverrides = overrides.filter((o) => o.tenant_id === tenant);
    if (tenantCandidates.length === 0) {
      out[tenant] = 0;
      continue;
    }
    const rate = tenantOverrides.length / tenantCandidates.length;
    out[tenant] = Math.round(rate * 10_000) / 10_000;
  }
  return out;
}

/**
 * Overlay live `nba_confidence_adjusted` events on top of the canned
 * timeline. For Phase 1 the live stores do not carry a history, so
 * the live overlay reads the current confidence on every NBA that
 * has `overrideCount > 0` and appends a single point at the end of
 * the canned series with that confidence.
 */
function computeConfidenceTimelinePerNba(
  fetchedAt: string,
): Record<string, ConfidencePoint[]> {
  const flat = allNbaCandidates();
  const out: Record<string, ConfidencePoint[]> = {};
  for (const [id, baseline] of Object.entries(
    DEMO_BASELINE_CONFIDENCE_TIMELINE,
  )) {
    out[id] = [...baseline];
  }
  // Append a live point for every NBA that has been overridden in
  // this session. Bound the per-id series at 32 points so the SVG
  // stays compact.
  for (const c of flat) {
    if (c.overrideCount > 0) {
      const series = out[c.id] ?? [];
      const lastTs =
        series.length > 0 ? series[series.length - 1].ts : fetchedAt;
      // Only append when the live point would advance the timeline,
      // so repeated snapshots without new overrides do not grow the
      // series unbounded.
      if (lastTs !== fetchedAt) {
        series.push({ ts: fetchedAt, confidence: c.confidence });
      } else if (series.length > 0) {
        // Replace the trailing point with the latest confidence.
        series[series.length - 1] = {
          ts: fetchedAt,
          confidence: c.confidence,
        };
      } else {
        series.push({ ts: fetchedAt, confidence: c.confidence });
      }
      out[c.id] = series.slice(-32);
    }
  }
  return out;
}

/**
 * Construct a Demo Mode metrics client. Cheap; no I/O at construction
 * time.
 */
export function createDemoMetricsClient(
  options: DemoMetricsClientOptions = {},
): MetricsClient {
  const now = options.now ?? (() => new Date().toISOString());

  const eventCountsByType: MetricsClient['eventCountsByType'] = async () => {
    const live = liveEventCounts();
    const out: Record<LiveLearningEventName, number> = {
      override_event: 0,
      mode_switch: 0,
      tenant_change: 0,
      nba_confidence_adjusted: 0,
      lab_basket_confirmation_required: 0,
    };
    for (const name of LIVE_LEARNING_EVENT_NAMES) {
      out[name] = DEMO_BASELINE_EVENT_COUNTS[name] + live[name];
    }
    return out;
  };

  const nbaStateRatio: MetricsClient['nbaStateRatio'] = async () =>
    computeNbaStateRatio();

  const beforeAfterByNba: MetricsClient['beforeAfterByNba'] = async () =>
    computeBeforeAfterByNba();

  const overrideRateByTenant: MetricsClient['overrideRateByTenant'] = async () =>
    computeOverrideRateByTenant();

  const overrideRateTrend: MetricsClient['overrideRateTrend'] = async () => {
    const trend: Record<TenantId, PersonalizationPoint[]> = {
      'apex-cardiometabolic': [
        ...DEMO_BASELINE_TENANT_TREND['apex-cardiometabolic'],
      ],
      'longevity-health-institute': [
        ...DEMO_BASELINE_TENANT_TREND['longevity-health-institute'],
      ],
    };
    return trend;
  };

  const confidenceTimelinePerNba: MetricsClient['confidenceTimelinePerNba'] =
    async () => computeConfidenceTimelinePerNba(now());

  const snapshot: MetricsClient['snapshot'] = async (window) => {
    const w = window ?? DEFAULT_TIME_WINDOW;
    const fetchedAt = now();
    const [counts, ratio, beforeAfter, rateByTenant, trend, timeline] =
      await Promise.all([
        eventCountsByType(w),
        nbaStateRatio(),
        beforeAfterByNba(),
        overrideRateByTenant(),
        overrideRateTrend(),
        confidenceTimelinePerNba(),
      ]);
    const snap: MetricsSnapshot = {
      window: w,
      fetchedAt,
      eventCounts: counts,
      nbaStateRatio: ratio,
      beforeAfterByNba: beforeAfter,
      overrideRateByTenant: rateByTenant,
      overrideRateTrend: trend,
      confidenceTimelinePerNba: timeline,
      // Plan v9 §4.2 prototype-anchored fields. Demo Mode emits the
      // canned fixtures verbatim so HC1 byte-identical reproducibility
      // holds across runs (the lists are frozen at module load).
      liveSignals: DEMO_LIVE_SIGNALS.map((s) => ({ ...s })),
      eventsIndexedCount: DEMO_EVENTS_INDEXED_COUNT,
      suggestionQualityRows: DEMO_SUGGESTION_QUALITY_ROWS.map((r) => ({ ...r })),
      clinicIntelligenceByTenant: {
        'apex-cardiometabolic': {
          ...DEMO_CLINIC_INTELLIGENCE_BY_TENANT['apex-cardiometabolic'],
          precisionMetrics:
            DEMO_CLINIC_INTELLIGENCE_BY_TENANT[
              'apex-cardiometabolic'
            ].precisionMetrics.map((m) => ({ ...m })),
        },
        'longevity-health-institute': {
          ...DEMO_CLINIC_INTELLIGENCE_BY_TENANT['longevity-health-institute'],
          precisionMetrics:
            DEMO_CLINIC_INTELLIGENCE_BY_TENANT[
              'longevity-health-institute'
            ].precisionMetrics.map((m) => ({ ...m })),
        },
      },
      learningBulletsByTenant: {
        'apex-cardiometabolic': DEMO_LEARNING_BULLETS_BY_TENANT[
          'apex-cardiometabolic'
        ].map((b) => ({ ...b })),
        'longevity-health-institute': DEMO_LEARNING_BULLETS_BY_TENANT[
          'longevity-health-institute'
        ].map((b) => ({ ...b })),
      },
      azureStackRows: DEMO_AZURE_STACK_ROWS.map((r) => ({ ...r })),
      hasLiveData: false,
    };
    return snap;
  };

  return {
    eventCountsByType,
    nbaStateRatio,
    beforeAfterByNba,
    overrideRateByTenant,
    overrideRateTrend,
    confidenceTimelinePerNba,
    snapshot,
  };
}
